import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth, { query } from './baseQuery.js';

/**
 * One API slice for the whole app.
 *
 * Tags replace the old `reload()` calls. Saving a vendor invalidates `Vendor`,
 * so the vendor table, the Stock In dropdown and anything else reading vendors
 * refetch themselves. Nothing has to remember to ask.
 *
 * `Report` is invalidated by every stock and price write, because a report is a
 * calculation over those histories rather than a stored number.
 */
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Auth', 'Setup', 'Catalog', 'Vendor', 'Customer', 'User', 'StockIn', 'StockOut', 'Price', 'Report'],

  endpoints: (build) => ({
    // -----------------------------------------------------------------------
    // Auth
    // -----------------------------------------------------------------------
    getSetup: build.query({
      query: () => '/auth/setup',
      transformResponse: (response) => response.data,
      providesTags: ['Setup'],
    }),

    getMe: build.query({
      query: () => '/auth/me',
      transformResponse: (response) => response.data,
      providesTags: ['Auth'],
    }),

    login: build.mutation({
      query: (credentials) => ({ url: '/auth/login', method: 'POST', body: credentials }),
      transformResponse: (response) => response.data,
      // A fresh session sees fresh everything.
      invalidatesTags: ['Auth', 'Catalog', 'Vendor', 'Customer', 'User', 'StockIn', 'StockOut', 'Price', 'Report'],
    }),

    register: build.mutation({
      query: (payload) => ({ url: '/auth/register', method: 'POST', body: payload }),
      // The first account signs straight in; later ones come back `pending`
      // with no user object, so keep both halves of the envelope.
      transformResponse: (response) => ({ user: response.data, meta: response.meta }),
      invalidatesTags: ['Auth', 'Setup', 'User'],
    }),

    logout: build.mutation({
      query: () => ({ url: '/auth/logout', method: 'POST', body: {} }),
    }),

    changePassword: build.mutation({
      query: (payload) => ({ url: '/auth/password', method: 'PATCH', body: payload }),
      transformResponse: (response) => response.meta,
    }),

    // -----------------------------------------------------------------------
    // Catalogs — `kind` is productName | texture | length | color
    // -----------------------------------------------------------------------
    listCatalog: build.query({
      query: ({ kind, activeOnly = false }) => `/catalog/${kind}${activeOnly ? '?status=active' : ''}`,
      transformResponse: (response) => response.data,
      // Both the full list and the active-only list carry the same tag, so one
      // write refreshes the management table and every dropdown at once.
      providesTags: (result, error, { kind }) => [{ type: 'Catalog', id: kind }],
    }),
    createCatalog: build.mutation({
      query: ({ kind, payload }) => ({ url: `/catalog/${kind}`, method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { kind }) => [{ type: 'Catalog', id: kind }],
    }),
    updateCatalog: build.mutation({
      query: ({ kind, id, payload }) => ({ url: `/catalog/${kind}/${id}`, method: 'PUT', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { kind }) => [{ type: 'Catalog', id: kind }, 'Report'],
    }),
    toggleCatalog: build.mutation({
      query: ({ kind, id }) => ({ url: `/catalog/${kind}/${id}/toggle`, method: 'PATCH' }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { kind }) => [{ type: 'Catalog', id: kind }],
    }),
    deleteCatalog: build.mutation({
      query: ({ kind, id }) => ({ url: `/catalog/${kind}/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { kind }) => [{ type: 'Catalog', id: kind }],
    }),

    // -----------------------------------------------------------------------
    // Vendors and Customers
    // -----------------------------------------------------------------------
    listVendors: build.query({
      query: ({ activeOnly = false } = {}) => `/vendors${activeOnly ? '?status=active' : ''}`,
      transformResponse: (response) => response.data,
      providesTags: ['Vendor'],
    }),
    createVendor: build.mutation({
      query: (payload) => ({ url: '/vendors', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Vendor'],
    }),
    updateVendor: build.mutation({
      query: ({ id, payload }) => ({ url: `/vendors/${id}`, method: 'PUT', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Vendor', 'Report'],
    }),
    toggleVendor: build.mutation({
      query: (id) => ({ url: `/vendors/${id}/toggle`, method: 'PATCH' }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Vendor'],
    }),
    deleteVendor: build.mutation({
      query: (id) => ({ url: `/vendors/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Vendor'],
    }),

    listCustomers: build.query({
      query: ({ activeOnly = false } = {}) => `/customers${activeOnly ? '?status=active' : ''}`,
      transformResponse: (response) => response.data,
      providesTags: ['Customer'],
    }),
    createCustomer: build.mutation({
      query: (payload) => ({ url: '/customers', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Customer'],
    }),
    updateCustomer: build.mutation({
      query: ({ id, payload }) => ({ url: `/customers/${id}`, method: 'PUT', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Customer', 'Price', 'Report'],
    }),
    toggleCustomer: build.mutation({
      query: (id) => ({ url: `/customers/${id}/toggle`, method: 'PATCH' }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Customer'],
    }),
    deleteCustomer: build.mutation({
      query: (id) => ({ url: `/customers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Customer'],
    }),

    // -----------------------------------------------------------------------
    // Users (admin)
    // -----------------------------------------------------------------------
    listUsers: build.query({
      query: () => '/users',
      transformResponse: (response) => response.data,
      providesTags: ['User'],
    }),
    listModules: build.query({
      query: () => '/users/modules',
      transformResponse: (response) => response.data,
    }),
    createUser: build.mutation({
      query: (payload) => ({ url: '/users', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['User'],
    }),
    updateUser: build.mutation({
      query: ({ id, payload }) => ({ url: `/users/${id}`, method: 'PUT', body: payload }),
      transformResponse: (response) => response.data,
      // Editing yourself changes what your own sidebar may show.
      invalidatesTags: ['User', 'Auth'],
    }),
    toggleUser: build.mutation({
      query: (id) => ({ url: `/users/${id}/toggle`, method: 'PATCH' }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['User'],
    }),
    approveUser: build.mutation({
      query: (id) => ({ url: `/users/${id}/approve`, method: 'PATCH', body: {} }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['User'],
    }),
    setUserPassword: build.mutation({
      query: ({ id, password }) => ({ url: `/users/${id}/password`, method: 'PATCH', body: { password } }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['User'],
    }),
    deleteUser: build.mutation({
      query: (id) => ({ url: `/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['User'],
    }),

    // -----------------------------------------------------------------------
    // Stock
    // -----------------------------------------------------------------------
    listStockIn: build.query({
      query: () => '/stock-in',
      transformResponse: (response) => response.data,
      providesTags: ['StockIn'],
    }),
    getStockInReceipt: build.query({
      query: (id) => `/stock-in/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'StockIn', id }],
    }),
    createStockIn: build.mutation({
      query: (payload) => ({ url: '/stock-in', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['StockIn', 'Report'],
    }),
    /**
     * Correcting a receipt rewrites the running balance on every later receipt
     * and every invoice issued since, so both lists have to refetch.
     */
    updateStockIn: build.mutation({
      query: ({ id, payload }) => ({ url: `/stock-in/${id}`, method: 'PUT', body: payload }),
      // `meta.repairedRows` says how many later records the replay had to rewrite,
      // so keep the envelope rather than unwrapping to the entity.
      transformResponse: (response) => ({ entry: response.data, repairedRows: response.meta?.repairedRows ?? 0 }),
      invalidatesTags: ['StockIn', 'StockOut', 'Report'],
    }),
    deleteStockIn: build.mutation({
      query: (id) => ({ url: `/stock-in/${id}`, method: 'DELETE' }),
      invalidatesTags: ['StockIn', 'StockOut', 'Report'],
    }),

    listStockOut: build.query({
      query: () => '/stock-out',
      transformResponse: (response) => response.data,
      providesTags: ['StockOut'],
    }),
    getInvoice: build.query({
      query: (id) => `/stock-out/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'StockOut', id }],
    }),
    /** Units on the shelf per SKU, for the Stock Out entry table. */
    getStockAvailability: build.query({
      query: () => '/stock-out/availability',
      transformResponse: (response) => response.data,
      providesTags: ['StockOut', 'StockIn'],
    }),
    createStockOut: build.mutation({
      query: (payload) => ({ url: '/stock-out', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      // Selling units changes what is on the shelf, so Stock In's "already sold"
      // delete guard and every report move with it.
      invalidatesTags: ['StockOut', 'StockIn', 'Report'],
    }),
    updateStockOut: build.mutation({
      query: ({ id, payload }) => ({ url: `/stock-out/${id}`, method: 'PUT', body: payload }),
      transformResponse: (response) => ({ invoice: response.data, repairedRows: response.meta?.repairedRows ?? 0 }),
      invalidatesTags: ['StockOut', 'StockIn', 'Report'],
    }),
    deleteStockOut: build.mutation({
      query: (id) => ({ url: `/stock-out/${id}`, method: 'DELETE' }),
      invalidatesTags: ['StockOut', 'StockIn', 'Report'],
    }),

    // -----------------------------------------------------------------------
    // Prices
    // -----------------------------------------------------------------------
    listGroupedPrices: build.query({
      query: () => '/prices/grouped',
      transformResponse: (response) => response.data,
      providesTags: ['Price'],
    }),
    /**
     * Called on demand while typing an invoice line, not on render — hence a
     * lazy query. `keepUnusedDataFor: 0` because a price the admin just changed
     * must not be served from cache into tonight's invoice.
     */
    lookupPrice: build.query({
      query: (params) => `/prices/lookup${query(params)}`,
      transformResponse: (response) => response.data,
      keepUnusedDataFor: 0,
    }),
    savePrices: build.mutation({
      query: (payload) => ({ url: '/prices', method: 'POST', body: payload }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Price'],
    }),
    deletePrice: build.mutation({
      query: (id) => ({ url: `/prices/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Price'],
    }),

    // -----------------------------------------------------------------------
    // Reports — `meta` carries the totals, so keep the whole envelope
    // -----------------------------------------------------------------------
    getDashboard: build.query({
      query: () => '/reports/dashboard',
      transformResponse: (response) => response.data,
      providesTags: ['Report'],
    }),
    getStockStatus: build.query({
      query: (params) => `/reports/stock-status${query(params)}`,
      transformResponse: (response) => ({ rows: response.data, meta: response.meta }),
      providesTags: ['Report'],
    }),
    getSalesReport: build.query({
      query: (params) => `/reports/sales${query(params)}`,
      transformResponse: (response) => ({ rows: response.data, meta: response.meta }),
      providesTags: ['Report'],
    }),
  }),
});

export const {
  useGetSetupQuery,
  useGetMeQuery,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useChangePasswordMutation,

  useListCatalogQuery,
  useCreateCatalogMutation,
  useUpdateCatalogMutation,
  useToggleCatalogMutation,
  useDeleteCatalogMutation,

  useListVendorsQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useToggleVendorMutation,
  useDeleteVendorMutation,

  useListCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useToggleCustomerMutation,
  useDeleteCustomerMutation,

  useListUsersQuery,
  useListModulesQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useToggleUserMutation,
  useApproveUserMutation,
  useSetUserPasswordMutation,
  useDeleteUserMutation,

  useListStockInQuery,
  useGetStockInReceiptQuery,
  useCreateStockInMutation,
  useUpdateStockInMutation,
  useDeleteStockInMutation,

  useListStockOutQuery,
  useGetInvoiceQuery,
  useGetStockAvailabilityQuery,
  useCreateStockOutMutation,
  useUpdateStockOutMutation,
  useDeleteStockOutMutation,

  useListGroupedPricesQuery,
  useLazyLookupPriceQuery,
  useSavePricesMutation,
  useDeletePriceMutation,

  useGetDashboardQuery,
  useGetStockStatusQuery,
  useGetSalesReportQuery,
} = apiSlice;